function requestHeader(config, name) {
    const headers = config?.headers;
    if (!headers) return undefined;
    if (typeof headers.get === "function") return headers.get(name);

    const headerKey = Object.keys(headers)
        .find((key) => key.toLowerCase() === name.toLowerCase());
    return headerKey ? headers[headerKey] : undefined;
}

// Mirrors the backend AUTHENTICATED_USER_PATHS: these endpoints operate on the
// authenticated user regardless of the active workspace, so a stray workspace
// header must not misclassify them as workspace-scoped mutations.
const AUTHENTICATED_USER_MUTATION_PATHS = [
    "/users",
    "/user",
    "/updateUser",
    "/option-set",
    "/user-event-logs",
    "/create-workspace",
    "/workspace-onboarding",
    "/accept-workspace-invitation",
    "/user-sessions",
];

function isAuthenticatedUserMutationPath(path = "") {
    return AUTHENTICATED_USER_MUTATION_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export const isWorkspaceScopedMutation = (config = {}) => {
    const method = String(config.method || "get").toLowerCase();
    const workspaceHeader = requestHeader(config, "workspace");
    if (!["post", "put", "patch", "delete"].includes(method)
        || workspaceHeader === null
        || workspaceHeader === undefined
        || String(workspaceHeader).trim() === "") {
        return false;
    }
    if (isAuthenticatedUserMutationPath(requestPath(config))) return false;
    return true;
};

function requestPath(config = {}) {
    const raw = String(config.url || "").split("?")[0];
    const normalized = raw.replace(/^https?:\/\/[^/]+/i, "");
    return normalized.startsWith("/api/") ? normalized.slice(4) : normalized;
}

function requestData(config = {}) {
    if (config.data && typeof config.data === "object") return config.data?.data;
    if (typeof config.data !== "string") return undefined;

    try {
        return JSON.parse(config.data)?.data;
    } catch {
        return undefined;
    }
}

function requestField(config = {}, name) {
    const data = config.data;
    if (data && typeof data.get === "function") return data.get(name);
    if (data && typeof data === "object" && !Array.isArray(data)) return data[name];
    return undefined;
}

export const isOwnWorkspacePreferenceMutation = (config = {}) => {
    const method = String(config.method || "get").toLowerCase();
    const path = requestPath(config);
    const data = requestData(config);
    const fields = data && typeof data === "object" && !Array.isArray(data)
        ? Object.keys(data)
        : [];

    return ["put", "patch"].includes(method)
        && /^\/workspace-memberships\/[^/]+$/.test(path)
        && fields.length > 0
        && fields.every((field) => ["isDefault", "workspaceConfigs"].includes(field));
};

export const isNormalWorkspaceMessagingMutation = (config = {}) => {
    const method = String(config.method || "get").toLowerCase();
    const path = requestPath(config);

    if (method === "post" && path === "/workspace-media/upload") {
        return String(requestField(config, "entityType") || "").trim() === "chat"
            && String(requestField(config, "entityDocumentId") || "").trim().length > 0
            && String(requestField(config, "purpose") || "").trim() === "attachment";
    }
    if (method === "post" && ["/chats", "/chat-messages"].includes(path)) return true;
    if (method === "post" && /^\/chat-messages\/[^/]+\/reaction$/.test(path)) return true;
    if (["put", "patch"].includes(method)) {
        return /^\/chats\/[^/]+(?:\/read)?$/.test(path)
            || /^\/chat-messages\/[^/]+$/.test(path)
            || /^\/chat-participants\/[^/]+$/.test(path);
    }
    return method === "delete" && (
        /^\/chats\/[^/]+$/.test(path)
        || /^\/chat-messages\/[^/]+$/.test(path)
    );
};

export const isWorkspaceMediaAccessMutation = (config = {}) => {
    const method = String(config.method || "get").toLowerCase();
    const path = requestPath(config);
    return method === "post" && /^\/workspace-media\/[^/]+\/access$/.test(path);
};

// Focus is an assigned member's personal activity. Viewer-level members may
// start and maintain their own sessions; the backend still validates tenant,
// membership, assignment, ownership, and optimistic session versions.
export const isPersonalFocusMutation = (config = {}) => {
    const method = String(config.method || "get").toLowerCase();
    const path = requestPath(config);
    if (!["post", "patch", "delete"].includes(method)) return false;

    return /^\/workspaces\/[^/]+\/focus-items(?:\/[^/]+|\/reorder)?$/.test(path)
        || /^\/workspaces\/[^/]+\/focus-sessions\/(?:start|[^/]+\/(?:pause|resume|reset|complete|note))$/.test(path);
};

// Task and milestone reports are member collaboration. Their dedicated
// controllers validate workspace visibility and restrict edits/deletes to the
// author (or a project manager), so the client must not pre-empt them for viewer
// workspace roles.
export const isMemberReportMutation = (config = {}) => {
    const method = String(config.method || "get").toLowerCase();
    const path = requestPath(config);
    if (!["post", "put", "patch", "delete"].includes(method)) return false;
    return /^\/(?:task-comments|deliverable-comments)(?:\/[^/]+)?$/.test(path);
};

export const isPaymentRecoveryMutation = (config = {}) => {
    const path = requestPath(config);
    return path === "/commerce-checkout"
        || path === "/commerce-checkout/quote"
        || path.startsWith("/payments/zarinpal/")
        || path === "/billing-profile/me";
};

export const isContextIndependentPaymentRecoveryMutation = (
    config = {},
    workspaceContextIndependent = false,
) => Boolean(workspaceContextIndependent) && isPaymentRecoveryMutation(config);

export const isContextIndependentWorkspaceOnboardingProvisioningMutation = (
    config = {},
    workspaceContextIndependent = false,
) => {
    if (!workspaceContextIndependent || String(config.method || "get").toLowerCase() !== "post") {
        return false;
    }

    return [
        "/projects",
        "/goals",
        "/create-workspace-invitation",
    ].includes(requestPath(config));
};
